import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CrownIcon, MonitorIcon, UsersIcon, CompassIcon, ArrowUpRightIcon, SparklesIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTranslations } from '../i18n/translations';
const itTags = ['React', 'Node.js', 'Python', 'Java', 'Go', 'DevOps', 'AWS/GCP', 'QA', 'iOS/Android', 'UI/UX', 'Data Science', 'Scrum Master'];
export function ServicesSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section className="relative py-20 lg:py-28 bg-white overflow-hidden" id="services" data-id="element-418">
      <div className="absolute inset-0 pointer-events-none" data-id="element-419">
        <div className="absolute top-20 right-0 w-[500px] h-[500px] rounded-full bg-primary-50/50 blur-3xl" data-id="element-420" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-cyan-50/40 blur-3xl" data-id="element-421" />
        <div className="absolute inset-0 opacity-[0.018]" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, rgba(15,23,42,1) 1px, transparent 0)`,
        backgroundSize: '48px 48px'
      }} data-id="element-422" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-423">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-14" data-id="element-424">
          <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-425">
            {t.services.eyebrow}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-426">
            {t.services.title}
          </h2>
          <p className="mt-4 text-lg text-navy-500 max-w-2xl mx-auto" data-id="element-427">
            {t.services.subtitle}
          </p>
        </motion.div>

        <div className="space-y-6" data-id="element-428">
          {/* Featured card — Executive Search */}
          <Link to="/services/executive-search" className="block" data-id="element-429">
            <motion.div initial={{
            opacity: 0,
            y: 24
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.55,
            delay: 0.1
          }} className="group relative rounded-2xl overflow-hidden cursor-pointer" data-id="element-430">
              <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900" data-id="element-431" />
              <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-primary-500/10 blur-3xl" data-id="element-432" />
              <div className="absolute bottom-0 left-1/4 w-[200px] h-[200px] rounded-full bg-cyan-500/8 blur-2xl" data-id="element-433" />
              <div className="absolute inset-0 opacity-[0.04]" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,1) 1px, transparent 0)`,
              backgroundSize: '28px 28px'
            }} data-id="element-434" />
              <div className="relative p-8 lg:p-10 flex flex-col lg:flex-row lg:items-center gap-8" data-id="element-435">
                <div className="flex-1" data-id="element-436">
                  <div className="flex items-center gap-3 mb-5" data-id="element-437">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-400 to-primary-600 text-white shadow-xl shadow-primary-500/30" data-id="element-438">
                      <CrownIcon className="w-7 h-7" data-id="element-439" />
                    </div>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary-500/20 text-primary-300 text-xs font-semibold rounded-full border border-primary-500/20" data-id="element-440">
                      <SparklesIcon className="w-3 h-3" data-id="element-441" />
                      {t.services.badge}
                    </span>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-extrabold text-white mb-3" data-id="element-442">
                    {t.services.card1_title}
                  </h3>
                  <p className="text-navy-300 leading-relaxed max-w-2xl" data-id="element-443">
                    {t.services.card1_desc}
                  </p>
                </div>
                <div className="flex-shrink-0 flex flex-row lg:flex-col gap-3" data-id="element-444">
                  <div className="px-5 py-3 rounded-xl bg-white/5 border border-white/10 text-center" data-id="element-445">
                    <div className="text-2xl font-extrabold text-white" data-id="element-446">
                      {t.services.card1_stat1_val}
                    </div>
                    <div className="text-xs text-navy-400 mt-0.5" data-id="element-447">
                      {t.services.card1_stat1_label}
                    </div>
                  </div>
                  <div className="px-5 py-3 rounded-xl bg-white/5 border border-white/10 text-center" data-id="element-448">
                    <div className="text-2xl font-extrabold text-primary-400" data-id="element-449">
                      {t.services.card1_stat2_val}
                    </div>
                    <div className="text-xs text-navy-400 mt-0.5" data-id="element-450">
                      {t.services.card1_stat2_label}
                    </div>
                  </div>
                </div>
                <ArrowUpRightIcon className="absolute top-6 right-6 w-5 h-5 text-navy-500 group-hover:text-primary-400 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" data-id="element-451" />
              </div>
            </motion.div>
          </Link>

          {/* Featured card — IT Recruitment */}
          <Link to="/services/it-recruitment" className="block" data-id="element-452">
            <motion.div initial={{
            opacity: 0,
            y: 24
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.55,
            delay: 0.18
          }} className="group relative rounded-2xl overflow-hidden cursor-pointer" data-id="element-453">
              <div className="absolute inset-0 bg-gradient-to-br from-violet-900 via-violet-800 to-navy-900" data-id="element-454" />
              <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-violet-500/12 blur-3xl" data-id="element-455" />
              <div className="absolute bottom-0 left-1/4 w-[200px] h-[200px] rounded-full bg-primary-500/8 blur-2xl" data-id="element-456" />
              <div className="absolute inset-0 opacity-[0.04]" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,1) 1px, transparent 0)`,
              backgroundSize: '28px 28px'
            }} data-id="element-457" />
              <div className="relative p-8 lg:p-10 flex flex-col lg:flex-row lg:items-center gap-8" data-id="element-458">
                <div className="flex-1" data-id="element-459">
                  <div className="flex items-center gap-3 mb-5" data-id="element-460">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-400 to-violet-600 text-white shadow-xl shadow-violet-500/30" data-id="element-461">
                      <MonitorIcon className="w-7 h-7" data-id="element-462" />
                    </div>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-violet-500/20 text-violet-300 text-xs font-semibold rounded-full border border-violet-500/20" data-id="element-463">
                      <SparklesIcon className="w-3 h-3" data-id="element-464" />
                      {t.services.badge}
                    </span>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-extrabold text-white mb-3" data-id="element-465">
                    {t.services.card2_title}
                  </h3>
                  <p className="text-violet-200/70 leading-relaxed max-w-2xl" data-id="element-466">
                    {t.services.card2_desc}
                  </p>
                  <div className="mt-5 flex flex-wrap gap-1.5" data-id="element-467">
                    {itTags.map(tag => <span key={tag} className="text-[10px] font-semibold px-2 py-0.5 bg-violet-500/15 text-violet-300 rounded-md border border-violet-500/20" data-id="element-468">
                        {tag}
                      </span>)}
                  </div>
                </div>
                <div className="flex-shrink-0 flex flex-row lg:flex-col gap-3" data-id="element-469">
                  <div className="px-5 py-3 rounded-xl bg-white/5 border border-white/10 text-center" data-id="element-470">
                    <div className="text-2xl font-extrabold text-white" data-id="element-471">14</div>
                    <div className="text-xs text-violet-300/70 mt-0.5" data-id="element-472">
                      днів у середньому
                    </div>
                  </div>
                  <div className="px-5 py-3 rounded-xl bg-white/5 border border-white/10 text-center" data-id="element-473">
                    <div className="text-2xl font-extrabold text-violet-400" data-id="element-474">
                      50+
                    </div>
                    <div className="text-xs text-violet-300/70 mt-0.5" data-id="element-475">
                      технологічних стеків
                    </div>
                  </div>
                </div>
                <ArrowUpRightIcon className="absolute top-6 right-6 w-5 h-5 text-violet-500/50 group-hover:text-violet-400 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" data-id="element-476" />
              </div>
            </motion.div>
          </Link>

          {/* Two cards row — secondary services */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-stretch" data-id="element-477">
            {/* Team building */}
            <Link to="/services/team-building" className="block" data-id="element-478">
              <motion.div initial={{
              opacity: 0,
              y: 24
            }} animate={isInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.5,
              delay: 0.28
            }} className="group relative p-7 rounded-2xl bg-white border border-navy-100 hover:border-primary-200 hover:shadow-xl hover:shadow-primary-100/50 transition-all duration-300 cursor-pointer overflow-hidden h-full" data-id="element-479">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-500 to-cyan-400 rounded-t-2xl" data-id="element-480" />
                <div className="absolute inset-0 bg-gradient-to-br from-primary-50/0 to-primary-50/0 group-hover:from-primary-50/40 group-hover:to-transparent transition-all duration-300 rounded-2xl" data-id="element-481" />
                <div className="relative" data-id="element-482">
                  <div className="flex items-start justify-between mb-5" data-id="element-483">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 text-white shadow-lg shadow-primary-500/25 group-hover:shadow-xl group-hover:shadow-primary-500/35 transition-shadow duration-300" data-id="element-484">
                      <UsersIcon className="w-6 h-6" data-id="element-485" />
                    </div>
                    <ArrowUpRightIcon className="w-4 h-4 text-navy-300 group-hover:text-primary-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" data-id="element-486" />
                  </div>
                  <h3 className="text-lg font-bold text-navy-900 mb-2 group-hover:text-primary-700 transition-colors" data-id="element-487">
                    {t.services.card3_title}
                  </h3>
                  <p className="text-sm text-navy-500 leading-relaxed" data-id="element-488">
                    {t.services.card3_desc}
                  </p>
                  <div className="mt-5 p-3 rounded-xl bg-primary-50/60 border border-primary-100/60" data-id="element-489">
                    <div className="text-lg font-extrabold text-primary-700" data-id="element-490">
                      {t.services.card3_case_val}
                    </div>
                    <div className="text-[11px] text-primary-500 font-medium" data-id="element-491">
                      {t.services.card3_case_label}
                    </div>
                  </div>
                </div>
              </motion.div>
            </Link>

            {/* Career consulting */}
            <Link to="/services/career-consulting" className="block" data-id="element-492">
              <motion.div initial={{
              opacity: 0,
              y: 24
            }} animate={isInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.5,
              delay: 0.36
            }} className="group relative p-7 rounded-2xl bg-white border border-navy-100 hover:border-amber-200 hover:shadow-xl hover:shadow-amber-100/50 transition-all duration-300 cursor-pointer overflow-hidden h-full" data-id="element-493">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 to-amber-400 rounded-t-2xl" data-id="element-494" />
                <div className="absolute inset-0 bg-gradient-to-br from-amber-50/0 to-amber-50/0 group-hover:from-amber-50/40 group-hover:to-transparent transition-all duration-300 rounded-2xl" data-id="element-495" />
                <div className="relative" data-id="element-496">
                  <div className="flex items-start justify-between mb-5" data-id="element-497">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-600 text-white shadow-lg shadow-amber-500/25 group-hover:shadow-xl group-hover:shadow-amber-500/35 transition-shadow duration-300" data-id="element-498">
                      <CompassIcon className="w-6 h-6" data-id="element-499" />
                    </div>
                    <ArrowUpRightIcon className="w-4 h-4 text-navy-300 group-hover:text-amber-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" data-id="element-500" />
                  </div>
                  <h3 className="text-lg font-bold text-navy-900 mb-2 group-hover:text-amber-700 transition-colors" data-id="element-501">
                    {t.services.card4_title}
                  </h3>
                  <p className="text-sm text-navy-500 leading-relaxed" data-id="element-502">
                    {t.services.card4_desc}
                  </p>
                  <div className="mt-5 flex items-center gap-2" data-id="element-503">
                    <div className="flex -space-x-1.5" data-id="element-504">
                      {['bg-amber-400', 'bg-orange-400', 'bg-rose-400'].map((c, i) => <div key={i} className={`w-6 h-6 rounded-full ${c} border-2 border-white`} data-id="element-505" />)}
                    </div>
                    <span className="text-xs text-navy-500 font-medium" data-id="element-506">
                      {t.services.card4_count}
                    </span>
                  </div>
                </div>
              </motion.div>
            </Link>
          </div>
        </div>
      </div>
    </section>;
}